import { Tab, TabGroup } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useGlobalAppCtxSelector } from '../../../../xstate/app.xstate';
import type { MaestroOrgPermissions } from './maestro-org-api';

export type OrgTab = 'details' | 'ai-models' | 'voice' | 'secrets';

/** Each tab is its own route under `edit`, so a link or reload lands on it. */
export const orgTabPath = (orgId: string, tab: OrgTab) =>
  tab === 'details'
    ? `/organization/${orgId}/edit`
    : `/organization/${orgId}/edit/${tab}`;

export const useMaestroOrgTabsEnabled = () =>
  useGlobalAppCtxSelector((ctx) =>
    Boolean(ctx.featureFlags?.['maestro-studio'])
  );

export const OrganizationTabs: FC<{
  active: OrgTab;
  /** When known, tabs this user can't view are left out, as the design does. */
  permissions?: MaestroOrgPermissions | null;
}> = ({ active, permissions }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { org_id: orgId = '' } = useParams();
  const maestro = useMaestroOrgTabsEnabled();

  // Details alone needs no tab bar; the Maestro tabs ship behind their flag.
  if (!maestro) return null;

  const all: { id: OrgTab; label: string }[] = [
    { id: 'details', label: t('manager/organizations:orgTabs.details') },
    { id: 'ai-models', label: t('manager/organizations:orgTabs.aiModels') },
    { id: 'voice', label: t('manager/organizations:orgTabs.voice') },
    { id: 'secrets', label: t('manager/organizations:orgTabs.secretsTab') },
  ];
  const tabs = all.filter(
    ({ id }) =>
      !permissions ||
      id === 'details' ||
      (id === 'secrets'
        ? permissions.canViewSecrets
        : permissions.canViewSettings)
  );

  return (
    <TabGroup placement="top" test-id="org-tabs">
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          slot="tab"
          panel={tab.id}
          active={tab.id === active}
          test-id={`org-tab-${tab.id}`}
          onClick={() => navigate(orgTabPath(orgId, tab.id), { replace: true })}
        >
          {tab.label}
        </Tab>
      ))}
    </TabGroup>
  );
};
